import React, { useState, useEffect } from 'react';
import { orderAPI, menuAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Clock, CheckCircle, AlertCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function OrderTracking() {
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuItems, setMenuItems] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 25;
  const [viewingOrder, setViewingOrder] = useState(null);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState('today'); // Default to today for kitchen
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Helper function to get date range
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
        }
        break;
      default:
        return null;
    }
    
    if (startDate && endDate) {
      return {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      };
    }
    return null;
  };

  useEffect(() => {
    fetchMenuItems();
    fetchOrders();
    setCurrentPage(1); // Reset to page 1 when filter changes
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, dateFilter, customStartDate, customEndDate]);

  const fetchMenuItems = async () => {
    try {
      const response = await menuAPI.getAll();
      if (response.data.success) {
        const itemsMap = {};
        response.data.data.forEach(item => {
          itemsMap[item._id] = item;
        });
        setMenuItems(itemsMap);
      }
    } catch (err) {
      console.error('Failed to fetch menu items', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange();
      let response;
      
      if (filterStatus === 'all') {
        response = await orderAPI.getAll(dateRange?.start, dateRange?.end);
      } else {
        response = await orderAPI.getByStatus(filterStatus);
        // Filter by date on client side for status-filtered results
        if (dateRange && response.data.success) {
          const start = new Date(dateRange.start);
          const end = new Date(dateRange.end);
          response.data.data = response.data.data.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate >= start && orderDate <= end;
          });
        }
      }
      
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update order status';
      toast.error(errorMessage);
      fetchOrders(); // Refresh to show current state
    }
  };

  const handlePrintKitchenOrder = async (order) => {
    try {
      toast.success('Printing kitchen order...');
      
      // Create a new window for printing with optimized thermal printer layout
      const printWindow = window.open('', '', 'width=300,height=600');
      const now = new Date();
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Kitchen Order</title>
            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
              
              body {
                font-family: 'Courier New', monospace;
                margin: 0;
                padding: 8px;
                font-size: 13px;
                line-height: 1.5;
                width: 80mm;
                background: white;
              }
              
              .header {
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                margin-bottom: 8px;
                border: 3px solid #000;
                padding: 8px;
                background: #f0f0f0;
              }
              
              .timestamp {
                text-align: center;
                font-size: 11px;
                margin-bottom: 8px;
                font-weight: bold;
              }
              
              .divider {
                border-top: 1px dashed #000;
                margin: 8px 0;
              }
              
              .bold-divider {
                border-top: 2px solid #000;
                margin: 10px 0;
              }
              
              .order-info {
                margin: 10px 0;
                font-size: 13px;
                line-height: 1.6;
              }
              
              .order-info div {
                margin: 3px 0;
              }
              
              .items {
                margin: 10px 0;
              }
              
              .item {
                margin: 10px 0;
                padding: 8px;
                background: #f5f5f5;
                border-left: 4px solid #333;
              }
              
              .item-name {
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 4px;
              }
              
              .item-qty {
                font-size: 13px;
                font-weight: bold;
                margin-top: 4px;
              }
              
              .footer {
                text-align: center;
                font-size: 12px;
                margin-top: 12px;
                padding-top: 8px;
                border-top: 2px solid #000;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="header">🍳 KITCHEN ORDER 🍳</div>
            <div class="timestamp">${now.toLocaleString()}</div>
            
            <div class="bold-divider"></div>
            
            <div class="order-info">
              <div><strong>Order #:</strong> ${order._id.substring(0, 8).toUpperCase()}</div>
              ${order.table_number ? `<div><strong>Table:</strong> ${order.table_number}</div>` : ''}
              <div><strong>Type:</strong> ${order.order_type === 'dine_in' ? '🍽️ DINE IN' : order.order_type === 'takeaway' ? '🎁 TAKEAWAY' : '🚚 DELIVERY'}</div>
              ${order.staff_name ? `<div><strong>Staff:</strong> ${order.staff_name}</div>` : ''}
            </div>
            
            <div class="bold-divider"></div>
            
            <div class="items">
              ${order.items.map(item => `
                <div class="item">
                  <div class="item-name">${menuItems[item.item_id]?.name || 'Item'}</div>
                  <div class="item-qty">Quantity: ${item.quantity}x</div>
                </div>
              `).join('')}
            </div>
            
            <div class="bold-divider"></div>
            
            <div class="footer">
              ⚡ PREPARE AND SERVE ⚡
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Auto print after a short delay to ensure content is loaded
      setTimeout(() => {
        printWindow.print();
      }, 250);
      
    } catch (err) {
      toast.error('Failed to print kitchen order');
      console.error(err);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      preparing: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      ready: 'bg-green-100 text-green-700 border-green-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'preparing':
        return <Clock className="w-5 h-5" />;
      case 'ready':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const statuses = ['preparing', 'ready'];

  // Group orders by status for summary
  const groupedOrders = {
    preparing: orders.filter(o => o.status === 'preparing'),
    ready: orders.filter(o => o.status === 'ready'),
  };

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && orders.length === 0) return <div className="p-8 text-center text-gray-600 text-lg">Loading orders...</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 h-full bg-gray-50">
      <div className="mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Kitchen KOT</h2>
        <p className="text-gray-600">Kitchen Order Tickets - Manage incoming orders</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-600 rounded-lg text-red-700 text-sm font-medium">
          {error}
        </div>
      )}

      {/* Status Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-red-600">{groupedOrders.preparing.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Preparing</p>
                <p className="text-2xl font-bold text-yellow-600">{groupedOrders.preparing.length}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ready</p>
                <p className="text-2xl font-bold text-green-600">{groupedOrders.ready.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Status Filters */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Filter by Status</p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All Orders ({orders.length})
                </Button>
                {statuses.map((status) => {
                  const count = orders.filter(o => o.status === status).length;
                  return (
                    <Button
                      key={status}
                      variant={filterStatus === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus(status)}
                      className="capitalize"
                    >
                      {status} ({count})
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Date Filters */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Filter by Date</p>
              <div className="flex gap-2 flex-wrap items-center">
                {['today', 'week', 'month', 'custom'].map((filter) => (
                  <Button
                    key={filter}
                    variant={dateFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateFilter(filter)}
                    className="capitalize"
                  >
                    {filter}
                  </Button>
                ))}
                
                {dateFilter === 'custom' && (
                  <>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-40"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-40"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KOT Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {currentOrders.map((order) => (
          <Card 
            key={order._id} 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setViewingOrder(order)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base">
                    #{order._id.substring(0, 8).toUpperCase()}
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    {order.table_number ? `Table ${order.table_number}` : order.order_type}
                  </p>
                </div>
                <Badge className={getStatusBadgeColor(order.status)}>
                  {order.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Items Preview */}
                <div className="text-sm">
                  <p className="font-medium text-gray-700 mb-1">Items:</p>
                  {order.items.slice(0, 2).map((item, idx) => (
                    <p key={idx} className="text-gray-600">
                      {menuItems[item.item_id]?.name || 'Item'} × {item.quantity}
                    </p>
                  ))}
                  {order.items.length > 2 && (
                    <p className="text-gray-500 text-xs mt-1">
                      +{order.items.length - 2} more items
                    </p>
                  )}
                </div>

                {/* Total Items Badge */}
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Items:</span>
                    <Badge variant="outline" className="text-lg font-bold">
                      {order.items.reduce((sum, item) => sum + item.quantity, 0)}
                    </Badge>
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  {new Date(order.created_at).toLocaleTimeString()}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Order Details Dialog */}
      <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Order #{viewingOrder?._id.substring(0, 8).toUpperCase()}
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {viewingOrder?.table_number && `Table ${viewingOrder.table_number} • `}
              {viewingOrder?.order_type === 'dine_in' ? 'Dine In' : viewingOrder?.order_type === 'takeaway' ? 'Takeaway' : 'Delivery'}
            </p>
          </DialogHeader>

          {viewingOrder && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex justify-center">
                <Badge className={`${getStatusBadgeColor(viewingOrder.status)} text-lg px-6 py-2`}>
                  <span className="mr-2">{getStatusIcon(viewingOrder.status)}</span>
                  {viewingOrder.status.toUpperCase()}
                </Badge>
              </div>

              {/* Order Time */}
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-gray-600">Order Time</p>
                  <p className="text-xl font-bold text-gray-800 mt-1">
                    {new Date(viewingOrder.created_at).toLocaleTimeString()}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date(viewingOrder.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Items to Prepare</h3>
                <div className="space-y-2">
                  {viewingOrder.items.map((item, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">
                              {menuItems[item.item_id]?.name || 'Item'}
                            </p>
                            {menuItems[item.item_id]?.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {menuItems[item.item_id]?.description}
                              </p>
                            )}
                          </div>
                          <Badge variant="default" className="text-xl px-4 py-2 ml-4">
                            ×{item.quantity}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Total Quantity */}
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-700">Total Items:</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {viewingOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Update Status</p>
                  <Select
                    value={viewingOrder.status}
                    onValueChange={(value) => {
                      handleStatusChange(viewingOrder._id, value);
                      setViewingOrder(null);
                    }}
                    disabled={viewingOrder.status === 'ready'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((status) => (
                        <SelectItem key={status} value={status} className="capitalize">
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {viewingOrder.status === 'ready' && (
                    <p className="text-xs text-orange-600 mt-2 text-center">
                      Order ready for checkout
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => handlePrintKitchenOrder(viewingOrder)}
                  className="w-full"
                  size="lg"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Kitchen Ticket
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </Button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  className="min-w-[40px]"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </Button>
        </div>
      )}

      {orders.length === 0 && (
        <Card className="text-center py-16">
          <CardContent>
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <p className="text-xl font-semibold text-gray-800">All orders completed!</p>
            <p className="text-gray-500 mt-2">No pending orders at this time</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
